import { useState } from "react";
import { Image, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { courseShadow, courseTheme } from "@/lib/training/courseTheme";
import type { Screen } from "@/lib/training/types";

type ScopeList = Extract<Screen, { kind: "scopeList" }>;

/**
 * The out-of-scope reference — one long scroll, nothing to answer.
 *
 * Each card names a single thing a PCA may not do, the catalog duty it hides
 * inside, why the line falls where it does, what remains assignable under that
 * duty, and who may do the barred part instead.
 *
 * The "you can still assign" block is not decoration. Without it a red banner
 * over a nail-care photo reads as "never assign nail care", when the duty is
 * perfectly assignable and only the boundary inside it is not.
 */
export function ScopeListScreen({ screen }: { screen: ScopeList }) {
    const questions = screen.questions ?? [];
    return (
        <View>
            {screen.intro ? <Text style={styles.intro}>{screen.intro}</Text> : null}

            <View style={styles.countRow}>
                <View style={styles.rule} />
                <Text style={styles.count}>{`${screen.items.length} things a PCA may not do`}</Text>
                <View style={styles.rule} />
            </View>

            {screen.items.map((item, i) => (
                <View key={i} style={styles.card}>
                    <Image source={{ uri: item.image }} style={styles.shot} resizeMode="cover" />

                    <View style={styles.stripe}>
                        <Text style={styles.stripeText}>NOT IN PCA SCOPE</Text>
                    </View>

                    <View style={styles.body}>
                        <Text style={styles.duty}>{`Sits inside: ${item.duty}`}</Text>
                        <Text style={styles.title}>{item.title}</Text>

                        <Text style={styles.lbl}>Why</Text>
                        <Text style={styles.why}>{item.why}</Text>

                        <View style={styles.okBox}>
                            <Text style={styles.okLbl}>You can still assign</Text>
                            <Text style={styles.ok}>{item.stillAllowed}</Text>
                        </View>

                        <View style={styles.whoBox}>
                            <Text style={styles.whoLbl}>Who can, instead</Text>
                            <Text style={styles.who}>{item.who}</Text>
                        </View>
                    </View>
                </View>
            ))}

            {questions.length > 0 ? (
                <View style={styles.quizWrap}>
                    <View style={styles.countRow}>
                        <View style={styles.rule} />
                        <Text style={styles.count}>Check yourself</Text>
                        <View style={styles.rule} />
                    </View>
                    <Text style={styles.quizLede}>
                        {"Ten duties an RN assigns by name. For each one, which answer describes " +
                            "what a PCA may actually do under it?"}
                    </Text>
                    {questions.map((q, i) => (
                        <Question key={i} n={i + 1} total={questions.length} q={q} />
                    ))}
                </View>
            ) : null}
        </View>
    );
}

type Q = NonNullable<Extract<Screen, { kind: "scopeList" }>["questions"]>[number];

/** One multiple choice card. Answering locks it and reveals what she may do. */
function Question({ n, total, q }: { n: number; total: number; q: Q }) {
    const [picked, setPicked] = useState<number | null>(null);
    const right = picked === q.answer;

    return (
        <View style={styles.qCard}>
            <Text style={styles.qNum}>{`Question ${n} of ${total} · ${q.duty}`}</Text>
            <Text style={styles.qPrompt}>{q.prompt}</Text>

            {q.options.map((opt, i) => {
                const chosen = picked === i;
                const isKey = i === q.answer;
                return (
                    <TouchableOpacity
                        key={i}
                        activeOpacity={0.85}
                        disabled={picked != null}
                        onPress={() => setPicked(i)}
                        style={[
                            styles.qOpt,
                            chosen && (isKey ? styles.qOptRight : styles.qOptWrong),
                            picked != null && !chosen && isKey && styles.qOptReveal,
                            picked != null && !chosen && !isKey && styles.qOptDim
                        ]}
                    >
                        <Text style={styles.qMark}>{["A", "B", "C", "D"][i] ?? ""}</Text>
                        <Text style={styles.qOptText}>{opt}</Text>
                    </TouchableOpacity>
                );
            })}

            {picked != null ? (
                <View style={[styles.qReveal, right ? styles.qRevealOk : styles.qRevealNo]}>
                    <Text style={[styles.qVerdict, right ? styles.qVerdictOk : styles.qVerdictNo]}>
                        {right ? "That's right" : "Not quite"}
                    </Text>
                    <Text style={styles.qExplain}>{q.explain}</Text>
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    intro: {
        fontWeight: "500",
        fontSize: 15,
        lineHeight: 23,
        color: courseTheme.slate,
        marginBottom: 18
    },
    countRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 20 },
    rule: { flex: 1, height: 1, backgroundColor: courseTheme.line },
    count: {
        fontWeight: "800",
        fontSize: 11,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: courseTheme.slateL
    },

    card: {
        backgroundColor: courseTheme.white,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: courseTheme.line,
        overflow: "hidden",
        marginBottom: 18,
        ...courseShadow.card
    },
    shot: { width: "100%", aspectRatio: 4 / 3, backgroundColor: courseTheme.paper },
    stripe: {
        backgroundColor: courseTheme.red,
        paddingVertical: 7,
        paddingHorizontal: 16
    },
    stripeText: {
        fontWeight: "800",
        fontSize: 11,
        letterSpacing: 1.2,
        color: courseTheme.white
    },

    body: { padding: 16 },
    duty: {
        fontWeight: "600",
        fontSize: 12,
        color: courseTheme.slateL,
        marginBottom: 5
    },
    title: {
        fontWeight: "800",
        fontSize: 19,
        lineHeight: 25,
        color: courseTheme.navy,
        marginBottom: 14
    },
    lbl: {
        fontWeight: "800",
        fontSize: 10.5,
        letterSpacing: 0.9,
        textTransform: "uppercase",
        color: courseTheme.slateL,
        marginBottom: 4
    },
    why: {
        fontWeight: "500",
        fontSize: 15.5,
        lineHeight: 23,
        color: courseTheme.slate,
        marginBottom: 14
    },
    okBox: {
        backgroundColor: courseTheme.greenTint,
        borderLeftWidth: 4,
        borderLeftColor: courseTheme.green,
        borderRadius: 12,
        padding: 13,
        marginBottom: 10
    },
    okLbl: {
        fontWeight: "800",
        fontSize: 10.5,
        letterSpacing: 0.9,
        textTransform: "uppercase",
        color: courseTheme.green,
        marginBottom: 4
    },
    ok: {
        fontWeight: "600",
        fontSize: 15.5,
        lineHeight: 22,
        color: courseTheme.navy
    },
    whoBox: {
        backgroundColor: courseTheme.tealTint,
        borderLeftWidth: 4,
        borderLeftColor: courseTheme.teal,
        borderRadius: 12,
        padding: 13
    },
    whoLbl: {
        fontWeight: "800",
        fontSize: 10.5,
        letterSpacing: 0.9,
        textTransform: "uppercase",
        color: courseTheme.tealD,
        marginBottom: 4
    },
    who: {
        fontWeight: "600",
        fontSize: 15.5,
        lineHeight: 22,
        color: courseTheme.navy
    },

    quizWrap: { marginTop: 14 },
    quizLede: {
        fontWeight: "500",
        fontSize: 15,
        lineHeight: 23,
        color: courseTheme.slate,
        marginBottom: 18
    },
    qCard: {
        backgroundColor: courseTheme.white,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: courseTheme.line,
        padding: 16,
        marginBottom: 16,
        ...courseShadow.card
    },
    qNum: {
        fontWeight: "800",
        fontSize: 11,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: courseTheme.tealD,
        marginBottom: 6
    },
    qPrompt: {
        fontWeight: "800",
        fontSize: 17,
        lineHeight: 24,
        color: courseTheme.navy,
        marginBottom: 14
    },
    qOpt: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 11,
        borderWidth: 2,
        borderColor: courseTheme.line,
        borderRadius: 13,
        padding: 13,
        marginBottom: 9,
        backgroundColor: courseTheme.white
    },
    qOptRight: { borderColor: courseTheme.green, backgroundColor: courseTheme.greenTint },
    qOptWrong: { borderColor: courseTheme.red, backgroundColor: courseTheme.redTint },
    qOptReveal: { borderColor: courseTheme.green, backgroundColor: courseTheme.greenTint },
    qOptDim: { opacity: 0.45 },
    qMark: {
        fontWeight: "800",
        fontSize: 12,
        color: courseTheme.slateL,
        marginTop: 2,
        width: 14
    },
    qOptText: {
        flex: 1,
        fontWeight: "600",
        fontSize: 15,
        lineHeight: 21,
        color: courseTheme.navy
    },
    qReveal: { borderRadius: 12, padding: 13, borderLeftWidth: 4, marginTop: 4 },
    qRevealOk: { backgroundColor: courseTheme.greenTint, borderLeftColor: courseTheme.green },
    qRevealNo: { backgroundColor: courseTheme.redTint, borderLeftColor: courseTheme.red },
    qVerdict: {
        fontWeight: "800",
        fontSize: 13,
        letterSpacing: 0.4,
        marginBottom: 4
    },
    qVerdictOk: { color: courseTheme.green },
    qVerdictNo: { color: courseTheme.red },
    qExplain: {
        fontWeight: "500",
        fontSize: 15,
        lineHeight: 22,
        color: courseTheme.navy
    }
});
